import { useCallback, useRef } from 'react'
import type { Connection } from '@/libs/workflow-node-ui'
import { useSelector } from '@/libs/state'
import { connectPhases, disconnectPhase } from '../application/connect-phases'
import { deletePhase } from '../application/manage-phase'
import { deleteStep } from '../application/manage-step'
import { updateTransition } from '../application/manage-transition'
import type { DisconnectPhaseInput, PhaseConnectionSlot } from '../domain/workflow'
import { activeWorkflow$, addingPhase$, addingStep$, allWorkflows$, canvasEdges$ } from '../store'
import { workflows as workflowsPort, notify } from '../wiring'

const SLOTS = ['right', 'bottom', 'left', 'top'] as const

const slotFromHandle = (handleId: string | null | undefined): PhaseConnectionSlot => {
	for (const slot of SLOTS) {
		if (handleId?.startsWith(slot)) return slot
	}
	return 'right'
}

type CanvasCallbacks = {
	initLayout: () => void
	refreshLayout: () => void
}

const useWorkflowCanvasActions = ({ initLayout, refreshLayout }: CanvasCallbacks) => {
	const workflow = useSelector(activeWorkflow$)
	// Track the last workflow id we initialized for, to detect identity changes
	const lastWorkflowIdRef = useRef<string | null>(null)

	if (workflow && workflow.id !== lastWorkflowIdRef.current) {
		lastWorkflowIdRef.current = workflow.id
		initLayout()
	}

	const syncWorkflow = useCallback((updated: typeof workflow) => {
		if (!updated) return
		allWorkflows$.set(prev => prev.map(w => (w.id === updated.id ? updated : w)))
		activeWorkflow$.set(updated)
	}, [])

	const refreshWorkflow = useCallback(async () => {
		const wf = activeWorkflow$.get()
		if (!wf) return
		const updated = await workflowsPort.getById(wf.id)
		if (!updated) return
		syncWorkflow(updated)
		refreshLayout()
	}, [syncWorkflow, refreshLayout])

	const handleAddPhase = useCallback(() => { addingPhase$.set(true) }, [])
	const handleAddStep = useCallback(() => { addingStep$.set(true) }, [])

	const handlePhaseConnect = useCallback(
		async (connection: Connection) => {
			const wf = activeWorkflow$.get()
			if (!wf) return
			const { source, target, sourceHandle } = connection
			if (!source || !target) return
			const slot = slotFromHandle(sourceHandle)
			const result = await connectPhases(
				workflowsPort,
				{ workflowId: wf.id, sourcePhaseId: source, slot, targetPhaseId: target },
				notify,
			)
			if (result) {
				syncWorkflow(result)
				refreshLayout()
			}
		},
		[syncWorkflow, refreshLayout],
	)

	const handlePhaseDisconnect = useCallback(
		async (input: DisconnectPhaseInput) => {
			const edgeId = `edge-phase-${input.sourcePhaseId}-${input.slot}-${input.targetPhaseId}`
			canvasEdges$.set(canvasEdges$.get().filter(e => e.id !== edgeId))
			const result = await disconnectPhase(workflowsPort, input, notify)
			if (result) {
				syncWorkflow(result)
				refreshLayout()
			}
		},
		[syncWorkflow, refreshLayout],
	)

	const handleTransitionConnect = useCallback(
		async (transitionId: string, targetStepId: string) => {
			const wf = activeWorkflow$.get()
			if (!wf) return
			const transition = wf.phases
				.flatMap(p => p.teamWorkflow.steps)
				.flatMap(s => s.transitions)
				.find(t => t.id === transitionId)
			if (!transition) return
			const result = await updateTransition(
				workflowsPort,
				{ id: transitionId, label: transition.label, trigger: transition.trigger, target: { kind: 'step', stepId: targetStepId } },
				notify,
			)
			if (result) {
				syncWorkflow(result)
				refreshLayout()
			}
		},
		[syncWorkflow, refreshLayout],
	)

	const handleTransitionDisconnect = useCallback(
		async (edgeId: string) => {
			const wf = activeWorkflow$.get()
			if (!wf) return
			const transitionId = edgeId.replace('edge-transition-', '')
			const transition = wf.phases
				.flatMap(p => p.teamWorkflow.steps)
				.flatMap(s => s.transitions)
				.find(t => t.id === transitionId)
			if (!transition) return
			canvasEdges$.set(canvasEdges$.get().filter(e => e.id !== edgeId))
			const result = await updateTransition(
				workflowsPort,
				{ id: transitionId, label: transition.label, trigger: transition.trigger, target: { kind: 'none' } },
				notify,
			)
			if (result) {
				syncWorkflow(result)
				refreshLayout()
			}
		},
		[syncWorkflow, refreshLayout],
	)

	const handleDeletePhase = useCallback(
		async (phaseId: string) => {
			const ok = await deletePhase(workflowsPort, phaseId, notify)
			if (ok) await refreshWorkflow()
		},
		[refreshWorkflow],
	)

	const handleDeleteStep = useCallback(
		async (stepId: string) => {
			const ok = await deleteStep(workflowsPort, stepId, notify)
			if (ok) await refreshWorkflow()
		},
		[refreshWorkflow],
	)

	return {
		handleAddPhase,
		handleAddStep,
		refreshWorkflow,
		handlePhaseConnect,
		handlePhaseDisconnect,
		handleTransitionConnect,
		handleTransitionDisconnect,
		handleDeletePhase,
		handleDeleteStep,
	}
}

export { useWorkflowCanvasActions }
